import { useState } from "react";
import { TableData } from "@/App/FinancialTransactions/Table";
import { CardView } from "@/App/FinancialTransactions/CardView";
import { AddFinancialTransactionBtn } from "@/App/FinancialTransactions/Add";
import {
  getFinancialTransactionsData,
  searchFinancialTransactionsData,
} from "@/Api/financialTransactions";
import { FinancialTransactionProps } from "@/types/financialTransaction";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { ApiDataProps } from "@/pages/Tenants";
import { TransactionFilters } from "@/App/FinancialTransactions/Filters";
import { CircleDollarSign, LayoutGrid, Table, WalletCards, Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Mask } from "@/utils/mask";
import { PageHeader } from "@/components/PageHeader";
import { useContextData } from "@/components/Context";

// Transaction types constants
const TRANSACTION_TYPES = {
  ACCOUNTS_PAYABLE: 1,
  ACCOUNTS_RECEIVABLE: 2,
};

export const PageList = () => {
  const [financialTransactions, setFinancialTransactions] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null,
    from: 1,
    to: 1,
    total: 0,
    per_page: 10,
  } as ApiDataProps<FinancialTransactionProps>);
  const [state, setState] = useState("loading");
  const [currentTab, setCurrentTab] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");
  const { hasPermission } = usePermissions();
  const { isNavbarHidden, setIsNavbarHidden } = useContextData();

  const [currentFilters, setCurrentFilters] = useState({});

  const pageActions = {
    next: async () => {
      if (
        financialTransactions?.current_page < financialTransactions?.last_page
      ) {
        setState("loading");

        try {
          let data;
          if (Object.keys(currentFilters).length > 0) {
            const result = await searchFinancialTransactionsData({
              ...currentFilters,
              page: financialTransactions?.current_page + 1
            });
            data = result.data;
          } else {
            const result = await getFinancialTransactionsData(
              financialTransactions?.current_page + 1
            );
            data = result.data;
          }
          setFinancialTransactions(data.response);
        } catch (error) {
          console.error("Error in next pagination:", error);
        } finally {
          setState("production");
        }
      }
      return;
    },
    previous: async () => {
      if (financialTransactions?.current_page > 1) {
        setState("loading");

        try {
          let data;
          if (Object.keys(currentFilters).length > 0) {
            const result = await searchFinancialTransactionsData({
              ...currentFilters,
              page: financialTransactions?.current_page - 1
            });
            data = result.data;
          } else {
            const result = await getFinancialTransactionsData(
              financialTransactions?.current_page - 1
            );
            data = result.data;
          }
          setFinancialTransactions(data.response);
        } catch (error) {
          console.error("Error in previous pagination:", error);
        } finally {
          setState("production");
        }
      }
      return;
    },
    lastPage: async () => {
      if (
        financialTransactions?.current_page !== financialTransactions?.last_page
      ) {
        setState("loading");

        try {
          let data;
          if (Object.keys(currentFilters).length > 0) {
            const result = await searchFinancialTransactionsData({
              ...currentFilters,
              page: financialTransactions?.last_page
            });
            data = result.data;
          } else {
            const result = await getFinancialTransactionsData(
              financialTransactions?.last_page
            );
            data = result.data;
          }
          setFinancialTransactions(data?.response);
        } catch (error) {
          console.error("Error in lastPage pagination:", error);
        } finally {
          setState("production");
        }
      }
      return;
    },
  };

  const handleFilters = (filters) => {
    if (currentTab !== "all") {
      filters.transaction_type_id =
        currentTab === "accounts_payable"
          ? TRANSACTION_TYPES.ACCOUNTS_PAYABLE
          : TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE;
    }

    setCurrentFilters(filters);
    
    return searchFinancialTransactionsData(filters);
  };

  const handleTabChange = async (value: string) => {
    setCurrentTab(value);
    setState("loading");

    const filters: any = {};

    if (value !== "all") {
      filters.transaction_type_id =
        value === "accounts_payable"
          ? TRANSACTION_TYPES.ACCOUNTS_PAYABLE
          : TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE;
    }

    setCurrentFilters(filters);

    try {
      const { data, ok } = await searchFinancialTransactionsData(filters);
      if (ok) {
        setFinancialTransactions(data?.response);
      }
    } catch (error) {
      console.error("Erro ao mudar aba:", error);
    } finally {
      setState("production");
    }
  };

  const getTabTitle = (tab: string) => {
    switch (tab) {
      case "accounts_payable":
        return (
          <div className="flex items-center gap-1">
            <WalletCards className="h-4 w-4 text-red-500" />
            <span
              className={
                currentTab === "accounts_payable"
                  ? "font-primary bg-primary/10 px-2 py-1 rounded-md"
                  : ""
              }
            >
              Contas a Pagar
            </span>
          </div>
        );
      case "accounts_receivable":
        return (
          <div className="flex items-center gap-1">
            <CircleDollarSign className="h-4 w-4 text-green-500" />
            <span
              className={
                currentTab === "accounts_receivable"
                  ? "font-primary bg-primary/10 px-2 py-1 rounded-md"
                  : ""
              }
            >
              Contas a Receber
            </span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1">
            <CircleDollarSign className="h-4 w-4" />
            <span
              className={
                currentTab === "all"
                  ? "font-primary bg-primary/10 px-2 py-1 rounded-md"
                  : ""
              }
            >
              Todas
            </span>
          </div>
        );
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "table" ? "cards" : "table");
  };

  return (
    <Main>
      <If condition={hasPermission("view-transactions-list")}>
        <PageHeader
          icon={CircleDollarSign}
          title="Transacoes Financeiras"
          description="Gerencie contas a pagar e receber"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsNavbarHidden(!isNavbarHidden)}
            className="w-9 p-0"
            title={
              isNavbarHidden
                ? "Mostrar menu lateral"
                : "Expandir tela (ocultar menu)"
            }
          >
            {isNavbarHidden ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleViewMode}
            className="w-9 p-0"
            title={
              viewMode === "table"
                ? "Mudar para visualização em cards"
                : "Mudar para visualização em tabela"
            }
          >
            {viewMode === "table" ? (
              <LayoutGrid className="h-4 w-4" />
            ) : (
              <Table className="h-4 w-4" />
            )}
          </Button>
          <If condition={hasPermission("create-transaction")}>
            <AddFinancialTransactionBtn
              updateFront={setFinancialTransactions}
            />
          </If>
        </PageHeader>

        <Tabs
          defaultValue="all"
          value={currentTab}
          onValueChange={handleTabChange}
          className="w-full mb-4"
        >
          <TabsList className="w-full grid grid-cols-3 bg-white rounded-lg dark:bg-gray-800">
            <TabsTrigger value="all">{getTabTitle("all")}</TabsTrigger>
            <TabsTrigger value="accounts_payable">
              {getTabTitle("accounts_payable")}
            </TabsTrigger>
            <TabsTrigger value="accounts_receivable">
              {getTabTitle("accounts_receivable")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4 pt-4">
            <div className="flex flex-col gap-4">
              <TransactionFilters
                updateFront={setFinancialTransactions}
                setState={setState}
                funcSearch={handleFilters}
                transactionType={undefined}
              />
            </div>
          </TabsContent>

          <TabsContent value="accounts_payable" className="space-y-4 pt-4">
            <div className="flex flex-col gap-4">
              <TransactionFilters
                updateFront={setFinancialTransactions}
                setState={setState}
                funcSearch={handleFilters}
                transactionType={TRANSACTION_TYPES.ACCOUNTS_PAYABLE}
              />
            </div>
          </TabsContent>

          <TabsContent value="accounts_receivable" className="space-y-4 pt-4">
            <div className="flex flex-col gap-4">
              <TransactionFilters
                updateFront={setFinancialTransactions}
                setState={setState}
                funcSearch={handleFilters}
                transactionType={TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE}
              />
            </div>
          </TabsContent>
        </Tabs>

        {state === "loading" ? (
          <CreateLoaderTable cols={6} lines={6} />
        ) : viewMode === "table" ? (
          <TableData
            financialTransactions={financialTransactions.data}
            updateFront={setFinancialTransactions}
            dataApi={financialTransactions}
            pageActions={pageActions}
          />
        ) : (
          <CardView
            financialTransactions={financialTransactions.data}
            updateFront={setFinancialTransactions}
            dataApi={financialTransactions}
            pageActions={pageActions}
          />
        )}

        {state !== "loading" && financialTransactions.total_amount && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <WalletCards className="h-5 w-5" />
              Resumo Financeiro
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {currentTab === "accounts_payable"
                    ? "Total Contas a Pagar"
                    : currentTab === "accounts_receivable"
                    ? "Total Contas a Receber"
                    : "Total Geral"}
                </div>
                <div
                  className={`text-2xl font-bold ${
                    currentTab === "accounts_payable"
                      ? "text-red-600 dark:text-red-400"
                      : currentTab === "accounts_receivable"
                      ? "text-green-600 dark:text-green-400"
                      : "text-blue-600 dark:text-blue-400"
                  }`}
                >
                  {Mask.formatCurrency(
                    parseFloat(financialTransactions.total_amount)
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </If>
    </Main>
  );
};
